import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { 
  FunnelIcon, 
  ArrowDownTrayIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  CheckIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline'
import { getUnpaidPayments, exportPain001 } from '../../api/payments'
import Card from '../../components/common/Card'
import Button from '../../components/common/Button'
import Input from '../../components/common/Input'
import Spinner from '../../components/common/Spinner'
import { useUIStore } from '../../stores/uiStore'
import PaymentExportModal from '../../components/payments/PaymentExportModal'

/**
 * PaymentsOverview Component
 * Main page for viewing and exporting unpaid payments
 */
export default function PaymentsOverview() {
  const { showToast } = useUIStore()
  const queryClient = useQueryClient()
  
  // State
  const [filters, setFilters] = useState({
    date_from: '',
    date_to: '',
    trainer_id: '',
    contract_id: '',
    amount_min: '',
    amount_max: '',
    contract_status: '',
    payment_frequency: '',
    search: ''
  })
  
  const [showFilters, setShowFilters] = useState(false)
  const [selectedPayments, setSelectedPayments] = useState([])
  const [showExportModal, setShowExportModal] = useState(false)
  
  // Fetch unpaid payments
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['unpaidPayments', filters],
    queryFn: () => getUnpaidPayments(filters),
    refetchOnWindowFocus: false
  })
  
  const payments = data?.payments || []
  const totalAmount = payments.reduce((sum, p) => sum + parseFloat(p.amount), 0)
  const selectedAmount = payments
    .filter(p => selectedPayments.includes(p.id))
    .reduce((sum, p) => sum + parseFloat(p.amount), 0)
  
  // Handle filter changes
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }))
  }
  
  // Clear all filters
  const clearFilters = () => {
    setFilters({
      date_from: '',
      date_to: '',
      trainer_id: '',
      contract_id: '',
      amount_min: '',
      amount_max: '',
      contract_status: '',
      payment_frequency: '',
      search: ''
    })
  }
  
  // Toggle payment selection
  const togglePaymentSelection = (paymentId) => {
    setSelectedPayments(prev => {
      if (prev.includes(paymentId)) {
        return prev.filter(id => id !== paymentId)
      } else {
        return [...prev, paymentId]
      }
    })
  }
  
  // Select all/none
  const toggleSelectAll = () => {
    if (selectedPayments.length === payments.length) {
      setSelectedPayments([])
    } else {
      setSelectedPayments(payments.map(p => p.id))
    }
  }
  
  // Handle export
  const handleExport = () => {
    if (selectedPayments.length === 0) {
      showToast('Please select at least one payment to export', 'error')
      return
    }
    setShowExportModal(true)
  }
  
  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount)
  }
  
  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('nl-NL')
  }
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Payments Overview</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage and export unpaid payments
          </p>
        </div>
        
        <div className="flex items-center space-x-3">
          <Button
            variant="secondary"
            onClick={() => setShowFilters(!showFilters)}
            leftIcon={<FunnelIcon className="h-5 w-5" />}
          >
            {showFilters ? 'Hide' : 'Show'} Filters
          </Button>
          
          <Button
            variant="primary"
            onClick={handleExport}
            disabled={selectedPayments.length === 0}
            leftIcon={<ArrowDownTrayIcon className="h-5 w-5" />}
          >
            Export PAIN.001 ({selectedPayments.length})
          </Button>
        </div>
      </div>
      
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <Card.Body>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Unpaid</p>
                <p className="mt-2 text-3xl font-semibold text-gray-900">
                  {payments.length}
                </p>
              </div>
              <div className="p-3 bg-blue-100 rounded-lg">
                <ExclamationTriangleIcon className="h-8 w-8 text-blue-600" />
              </div>
            </div>
          </Card.Body>
        </Card>
        
        <Card>
          <Card.Body>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Amount</p>
                <p className="mt-2 text-3xl font-semibold text-gray-900">
                  {formatCurrency(totalAmount)}
                </p>
              </div>
            </div>
          </Card.Body>
        </Card>
        
        <Card>
          <Card.Body>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Selected Amount</p>
                <p className="mt-2 text-3xl font-semibold text-primary-600">
                  {formatCurrency(selectedAmount)}
                </p>
              </div>
              <div className="p-3 bg-primary-100 rounded-lg">
                <CheckIcon className="h-8 w-8 text-primary-600" />
              </div>
            </div>
          </Card.Body>
        </Card>
      </div>
      
      {/* Filters */}
      {showFilters && (
        <Card>
          <Card.Header>
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Filters</h2>
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                Clear All
              </Button>
            </div>
          </Card.Header>
          <Card.Body>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Search */}
              <div className="md:col-span-3">
                <Input
                  label="Search"
                  placeholder="Search by contract, trainer name, or email..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  leftIcon={<MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />}
                />
              </div>
              
              {/* Date Range */}
              <Input
                label="Date From"
                type="date"
                value={filters.date_from}
                onChange={(e) => handleFilterChange('date_from', e.target.value)}
              />
              
              <Input
                label="Date To"
                type="date"
                value={filters.date_to}
                onChange={(e) => handleFilterChange('date_to', e.target.value)}
              />
              
              {/* Contract Status */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Contract Status
                </label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                  value={filters.contract_status}
                  onChange={(e) => handleFilterChange('contract_status', e.target.value)}
                >
                  <option value="">All Statuses</option>
                  <option value="draft">Draft</option>
                  <option value="pending">Pending</option>
                  <option value="signed">Signed</option>
                </select>
              </div>
              
              {/* Amount Range */}
              <Input
                label="Min Amount (€)"
                type="number"
                step="0.01"
                placeholder="0.00"
                value={filters.amount_min}
                onChange={(e) => handleFilterChange('amount_min', e.target.value)}
              />
              
              <Input
                label="Max Amount (€)"
                type="number"
                step="0.01"
                placeholder="10000.00"
                value={filters.amount_max}
                onChange={(e) => handleFilterChange('amount_max', e.target.value)}
              />
              
              {/* Payment Frequency */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Payment Frequency
                </label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                  value={filters.payment_frequency}
                  onChange={(e) => handleFilterChange('payment_frequency', e.target.value)}
                >
                  <option value="">All Frequencies</option>
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="yearly">Yearly</option>
                  <option value="twice_yearly">Twice Yearly</option>
                  <option value="manual">Manual</option>
                </select>
              </div>
            </div>
          </Card.Body>
        </Card>
      )}
      
      {/* Payments Table */}
      <Card>
        <Card.Body className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Spinner size="lg" />
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-red-600">Error loading payments: {error.message}</p>
            </div>
          ) : payments.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500">No unpaid payments found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={selectedPayments.length === payments.length}
                        onChange={toggleSelectAll}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Due Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contract
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Trainer
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Payment #
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      IBAN
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {payments.map((payment) => (
                    <tr 
                      key={payment.id}
                      className={`hover:bg-gray-50 ${selectedPayments.includes(payment.id) ? 'bg-primary-50' : ''}`}
                    >
                      <td className="px-6 py-4">
                        <input
                          type="checkbox"
                          checked={selectedPayments.includes(payment.id)}
                          onChange={() => togglePaymentSelection(payment.id)}
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(payment.scheduled_date)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        <div className="font-medium">{payment.contract_title}</div>
                        <div className="text-gray-500 text-xs">{payment.contract_uid}</div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {payment.trainer_first_name} {payment.trainer_last_name}
                        <div className="text-gray-500 text-xs">{payment.trainer_email}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        #{payment.payment_number}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-right text-gray-900">
                        {formatCurrency(payment.amount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center text-sm">
                        {payment.trainer_iban ? (
                          <CheckIcon className="h-5 w-5 text-green-500 mx-auto" />
                        ) : (
                          <XMarkIcon className="h-5 w-5 text-red-500 mx-auto" />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card.Body>
      </Card>
      
      {/* Export Modal */}
      {showExportModal && (
        <PaymentExportModal
          paymentIds={selectedPayments}
          payments={payments.filter(p => selectedPayments.includes(p.id))}
          onClose={() => setShowExportModal(false)}
          onSuccess={() => {
            setShowExportModal(false)
            setSelectedPayments([])
            refetch()
          }}
        />
      )}
    </div>
  )
}